import React, { useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Image,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import SearchInput from "@/shared/components/search-input";
import CenteredSpinner from "@/shared/components/centered-spinner";
import CustomNoResults from "@/shared/components/no-results/CustomNoResults";
import CustomIcon, { AssetTypes } from "../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickPagination from "@/features/pathpick/components/ui/PathPickPagination";
import PathPickCopyableSkuText from "@/features/pathpick/components/product/PathPickCopyableSkuText";
import PathPickRequestStatusChip from "@/features/pathpick/components/request/PathPickRequestStatusChip";
import PathPickRequestFilterTabs from "@/features/pathpick/components/request/PathPickRequestFilterTabs";
import PathPickRequestFilterModal from "@/features/pathpick/components/request/PathPickRequestFilterModal";
import PathPickManageProductModal from "@/features/pathpick/components/request/PathPickManageProductModal";
import PathPickUnsyncConfirmModal from "@/features/pathpick/components/request/PathPickUnsyncConfirmModal";
import PathPickRetryConfirmModal from "@/features/pathpick/components/request/PathPickRetryConfirmModal";
import useGetPathPickProductRequests from "@/features/pathpick/features/sync-download-history/hooks/query/useGetPathPickProductRequests";
import usePathPickUnsyncProducts from "@/features/pathpick/hooks/usePathPickUnsyncProducts";
import usePathPickRetryRequests, {
  PathPickRetryTarget,
} from "@/features/pathpick/hooks/usePathPickRetryRequests";
import {
  formatRequestDateTime,
  getPathPickSyncStatusMeta,
  getProductRequestChannelStatusMeta,
  getTotalOptionQuantity,
  getFailureNoteKey,
  isChannelResultFailed,
  type PathPickFailureNoteKey,
  isChannelResultRetryable,
  isProductSyncActive,
  toProductRequestChannelResults,
  toRetryableKinds,
} from "@/features/pathpick/utils/request";
import {
  PathPickProductRequestChannel,
  PathPickProductRequestChannelResult,
  PathPickUnsyncTarget,
} from "@/features/pathpick/types";
import {
  PathPickMutationProduct,
  PathPickProductKey,
  PathPickProductRequestKind,
  PathPickProductType,
} from "@/features/pathpick/types";
import { toPathPickProductKey } from "@/features/pathpick/utils";
import {
  IS_REQUEST_RETRY_ENABLED,
  PathPickRequestStatusTab,
  REQUEST_STATUS_TABS,
  REQUEST_STATUS_TAB_LABEL_KEY,
  toStatusFilterParam,
} from "@/features/pathpick/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// 선택 / 상품 / 현재 재고 / 구분 / 상태 / 비고 / 신청일 / 최근 업데이트 / 관리
// 선택·관리 열은 회원 종류와 무관하게 항상 둔다 — 파일 전용 회원도 실패 건을 재생성할 수 있다.
const COLUMNS = [
  "select",
  "product",
  "stock",
  "kind",
  "status",
  "note",
  "requestedAt",
  "updatedAt",
  "manage",
] as const;

type RequestColumn = (typeof COLUMNS)[number];

// 열 간격은 grid gap이 아니라 셀 패딩(CELL_PADDING_RIGHT)으로 준다 —
// gap을 쓰면 채널 줄 사이 구분선이 열 경계마다 끊긴다.
const COLUMN_WIDTH: Record<RequestColumn, string> = {
  select: "48px",
  product: "minmax(276px, 1.6fr)",
  stock: "100px",
  kind: "196px",
  status: "166px",
  note: "minmax(136px, 1fr)",
  requestedAt: "156px",
  updatedAt: "156px",
  manage: "140px",
};

const CELL_PADDING_RIGHT = "16px";

// 채널 줄 높이 — 관리 버튼(AppButton 기본 28px)이 있는 줄만 높아지지 않게 행 트랙으로 못박는다.
// 비고 문구가 2줄(13px/20px)까지 흘러도 이 높이 안에 들어온다.
const CHANNEL_ROW_HEIGHT = "40px";

// 헤더 라벨 키 — 선택 열은 라벨 대신 전체 선택 체크박스가 들어간다.
const COLUMN_LABEL_KEY: Record<RequestColumn, string | null> = {
  select: null,
  product: "column_product",
  stock: "column_stock",
  kind: "column_kind",
  status: "column_status",
  note: "column_note",
  requestedAt: "column_requested_at",
  updatedAt: "column_updated_at",
  manage: "column_manage",
};

// 숫자 열은 가운데 정렬한다 — 헤더와 값이 갈리지 않게 열 단위로 한 번만 정한다.
const COLUMN_TEXT_ALIGN: Partial<Record<RequestColumn, "center">> = {
  stock: "center",
};

// 전체 / API 연동 / 스마트스토어 파일 / KREAM 파일
type RequestFilter = "all" | "apiSync" | "smartStoreFile" | "kreamFile";
const REQUEST_FILTERS: RequestFilter[] = [
  "all",
  "apiSync",
  "smartStoreFile",
  "kreamFile",
];

const FILTER_KEY = "path_pick:sync_download_history.product_requests_modal.";

const FILTER_LABEL_KEY: Record<RequestFilter, string> = {
  all: `${FILTER_KEY}filter_all`,
  apiSync: `${FILTER_KEY}filter_api_sync`,
  smartStoreFile: `${FILTER_KEY}filter_smart_store_file`,
  kreamFile: `${FILTER_KEY}filter_kream_file`,
};

// 필터 → 서버 request_type__in (전체는 파라미터 없음)
const FILTER_REQUEST_KINDS: Record<
  RequestFilter,
  PathPickProductRequestKind[] | undefined
> = {
  all: undefined,
  apiSync: ["NAVER_SYNC"],
  smartStoreFile: ["NAVER_EXCEL"],
  // 주문 확정 수량 신청도 KREAM 파일이라 함께 남긴다 — 표시는 한 줄로 합쳐진다.
  kreamFile: ["KREAM_EXCEL", "KREAM_EXCEL_CONFIRMED_QUANTITY"],
};

// 필터가 걸려도 아이템에는 슬롯이 모두 실려 오므로 표시 채널은 별도로 좁힌다.
const FILTER_CHANNELS: Record<
  RequestFilter,
  PathPickProductRequestChannel[] | null
> = {
  all: null, // 전체는 필터하지 않는다
  apiSync: ["smartStoreSync"],
  smartStoreFile: ["naverExcel"],
  kreamFile: ["kreamExcel"],
};

// 구분 문구 앞 아이콘 — 장바구니 선택 바의 신청 버튼과 같은 아이콘을 써서
// API 연동(upload-cloud)과 파일 다운로드(download) 줄이 글자를 읽기 전에 갈리게 한다.
const CHANNEL_ICON: Record<PathPickProductRequestChannel, AssetTypes> = {
  smartStoreSync: "upload-cloud",
  naverExcel: "download",
  kreamExcel: "download",
};

const CHANNEL_LABEL_KEY: Record<PathPickProductRequestChannel, string> = {
  smartStoreSync: "channel_smart_store_sync",
  naverExcel: "channel_naver_excel",
  kreamExcel: "channel_kream_excel",
};

const DEFAULT_PAGE_SIZE = 20;

// 선택 항목 — 페이지를 넘겨도 선택이 유지되므로 일괄 버튼 노출·카운트 판정에 필요한
// 능력치(연동 해제 가능 / 재생성 대상 채널)까지 함께 들고 있는다.
// 현재 페이지 목록에서 되찾으면 다른 페이지 선택분의 능력치를 알 수 없다.
interface SelectedProduct extends PathPickMutationProduct {
  canUnsync: boolean;
  retryKinds: PathPickProductRequestKind[];
}

// 선택 키 — 단품오더와 라인시트 상품은 숫자 id가 겹쳐 유형까지 붙인다.
const toSelectionKey = ({ id, type }: PathPickMutationProduct) =>
  toPathPickProductKey(type, id);

// 셀 배치(열 번호) + 열 간격을 함께 돌려준다. 마지막 열은 오른쪽 여백을 두지 않는다.
const cellProps = (column: RequestColumn) => {
  const index = COLUMNS.indexOf(column);
  return {
    gridColumn: index + 1,
    paddingRight: index === COLUMNS.length - 1 ? undefined : CELL_PADDING_RIGHT,
    textAlign: COLUMN_TEXT_ALIGN[column],
  };
};

function PathPickSyncDownloadProductRequestsModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();

  // 체험·멤버십 만료 시 재생성·연동 해제 같은 쓰기 동작만 잠근다(조회·검색은 그대로).
  const { isMembershipExpired } = useMembership();
  const lockedProps = isMembershipExpired
    ? {
        isDisabled: true,
        disabledMessage: t("path_pick:membership_lock.disabled_tooltip"),
      }
    : undefined;

  // 입력값(search)과 서버로 보내는 커밋값(query)을 분리해 타이핑마다 요청이 나가지 않게 한다.
  const [search, setSearch] = useState<string>("");
  const [query, setQuery] = useState<string>("");
  const [requestFilter, setRequestFilter] = useState<RequestFilter>("all");
  // 처리 상태 탭 — 구분(요청 종류)과 다른 축이라 별도 상태로 둔다.
  const [statusTab, setStatusTab] = useState<PathPickRequestStatusTab>("ALL");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [selectedProducts, setSelectedProducts] = useState<
    Map<PathPickProductKey, SelectedProduct>
  >(new Map());
  // 개별 관리 모달 대상 (null이면 닫힘)
  const [manageTarget, setManageTarget] = useState<PathPickUnsyncTarget | null>(
    null
  );
  const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState<boolean>(false);
  // 모바일 필터 모달 — 좁은 폭에서 인라인 필터 줄 대신 이걸 띄운다.
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  // 재생성 확인 모달 대상 — 단건은 1개, 일괄은 선택분 전체가 들어온다 (null이면 닫힘).
  const [retryTargets, setRetryTargets] = useState<
    PathPickRetryTarget[] | null
  >(null);
  // 모바일(md 미만)은 표 칼럼 합이 화면을 한참 넘어 가로 스크롤로만 읽히므로 상품 1건을 카드 1장으로,
  // 채널 줄은 카드 안의 하위 블록으로 그린다. 가로 스크롤은 뷰포트 폭 문제라 브레이크포인트로 가른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const { unsyncProducts, isPending: isUnsyncing } =
    usePathPickUnsyncProducts();
  const { retryRequests, isPending: isRetrying } = usePathPickRetryRequests();

  const { pathPickProductRequests, isPending, isPlaceholderData } =
    useGetPathPickProductRequests(
      {
        requestType__in: FILTER_REQUEST_KINDS[requestFilter],
        status__in: toStatusFilterParam(statusTab),
        query: query || undefined,
        pageNumber,
        pageSize,
      },
      isOpen
    );

  const totalCount = pathPickProductRequests?.totalCount ?? 0;
  // 연동 해제는 연동 허용 회원에게만 노출한다 — 파일 전용 회원에겐 해제할 연동이 없다.
  const canUnsync = pathPickProductRequests?.allowNaverSync ?? false;

  // 재생성을 닫아 둔 동안 일괄 처리 영역에 남는 동작은 연동 해제뿐이라, 해제가 불가능한
  // 회원에게는 선택 열과 상단 액션 줄을 통째로 두지 않는다(고를 수 있는 행이 없다).
  // 열 인덱스는 그대로 두고 트랙 폭만 0으로 접는다 — cellProps의 열 계산을 건드리지 않기 위해서다.
  const templateColumns = COLUMNS.map((column) =>
    column === "select" && !canUnsync ? "0px" : COLUMN_WIDTH[column]
  ).join(" ");

  const allowedChannels = FILTER_CHANNELS[requestFilter];

  // 상품별 표시 채널 결과 + 행 단위 가능 동작 계산 (검색/필터/페이지네이션은 서버가 처리한다)
  const items = (pathPickProductRequests?.items ?? []).map((item) => {
    const channelResults = toProductRequestChannelResults(item);
    const visibleResults = allowedChannels
      ? channelResults.filter((result) =>
          allowedChannels.includes(result.channel)
        )
      : channelResults;
    // 상품 참조가 없는 행은 요청 body에 담을 수 없어 어떤 동작도 걸 수 없다.
    const hasProductRef = item.productId !== null && item.productType !== null;
    // 재생성 대상은 화면에 보이는 채널 줄에서만 뽑는다 — 구분 필터로 가린 채널까지 다시 신청하지 않는다.
    // 재생성 자체를 닫아 둔 동안(IS_REQUEST_RETRY_ENABLED)은 항상 빈 배열이다.
    const retryKinds =
      IS_REQUEST_RETRY_ENABLED && hasProductRef
        ? toRetryableKinds(visibleResults)
        : [];
    const canUnsyncItem =
      canUnsync && hasProductRef && isProductSyncActive(item);

    return {
      ...item,
      visibleResults,
      retryKinds,
      canUnsyncItem,
      isSelectable: canUnsyncItem || retryKinds.length > 0,
    };
  });

  // 선택 가능한 행 — 전체 선택/부분 선택 판정과 선택 항목 조립에 함께 쓴다.
  const selectableProducts: SelectedProduct[] = items
    .filter((item) => item.isSelectable)
    .map((item) => ({
      id: item.productId as number,
      type: item.productType as PathPickProductType,
      canUnsync: item.canUnsyncItem,
      retryKinds: item.retryKinds,
    }));
  const isAllSelected =
    selectableProducts.length > 0 &&
    selectableProducts.every((product) =>
      selectedProducts.has(toSelectionKey(product))
    );
  const isIndeterminate =
    !isAllSelected &&
    selectableProducts.some((product) =>
      selectedProducts.has(toSelectionKey(product))
    );

  // 선택분 중 각 기능이 실제로 적용되는 항목만 추린다 — 섞여 있으면 버튼은 둘 다 뜨지만 대상은 갈린다.
  const selectedList = Array.from(selectedProducts.values());
  const bulkUnsyncProducts: PathPickMutationProduct[] = selectedList
    .filter((product) => product.canUnsync)
    .map(({ id, type }) => ({ id, type }));
  const bulkRetryTargets: PathPickRetryTarget[] = selectedList
    .filter((product) => product.retryKinds.length > 0)
    .map((product) => ({
      product: { id: product.id, type: product.type },
      kinds: product.retryKinds,
    }));

  // 하나라도 선택돼 있으면(전체/부분 모두) 누를 때 비우고, 아무것도 없을 때만 현재 페이지를 전체 선택한다.
  const hasSelection = selectedProducts.size > 0;
  // 버튼 노출은 "현재 목록에 가능한 행이 있거나, 선택분에 대상이 있을 때" — 선택분까지 보는 게 핵심이다.
  // 목록만 보면 다른 페이지에서 고른 선택분을 처리할 버튼이 사라진다.
  const showRetry =
    bulkRetryTargets.length > 0 ||
    selectableProducts.some((product) => product.retryKinds.length > 0);
  // 연동 해제는 행이 아니라 회원 단위로 판정한다 — 마지막 연동 상품을 해제해도 버튼이 비활성으로 남아
  // 액션 줄 높이가 줄지 않는다(버튼이 빠지면 그만큼 아래 표가 늘어나 화면이 출렁인다).
  const showUnsync = canUnsync;

  const handleToggleAll = () => {
    setSelectedProducts((prev) => {
      if (isAllSelected || isIndeterminate) {
        return new Map<PathPickProductKey, SelectedProduct>();
      }
      const next = new Map(prev);
      selectableProducts.forEach((product) =>
        next.set(toSelectionKey(product), product)
      );
      return next;
    });
  };

  const handleToggleProduct = (product: SelectedProduct) => {
    setSelectedProducts((prev) => {
      const next = new Map(prev);
      const key = toSelectionKey(product);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, product);
      }
      return next;
    });
  };

  // 벌크 — 확인 모달에서 연동만 해제 / 상품 삭제를 고른다.
  // 해제한 상품만 선택에서 뺀다 — 재생성만 가능한 선택분은 그대로 남겨 이어서 처리할 수 있게 한다.
  const handleBulkUnsync = (deleteFromStore: boolean) => {
    const unsyncedKeys = bulkUnsyncProducts.map(toSelectionKey);
    unsyncProducts(bulkUnsyncProducts, deleteFromStore, {
      onSuccess: () => {
        setSelectedProducts((prev) => {
          const next = new Map(prev);
          unsyncedKeys.forEach((key) => next.delete(key));
          return next;
        });
        setIsBulkConfirmOpen(false);
      },
    });
  };

  // 단건 — 관리 모달에서 연동만 해제 / 상품 삭제를 고른다.
  const handleManageUnsync = (deleteFromStore: boolean) => {
    if (!manageTarget) {
      return;
    }
    const { id, type } = manageTarget;
    unsyncProducts([{ id, type }], deleteFromStore, {
      onSuccess: () => {
        // 해제된 상품은 더 이상 선택 대상이 아니므로 선택 목록에서도 뺀다.
        setSelectedProducts((prev) => {
          const next = new Map(prev);
          next.delete(toPathPickProductKey(type, id));
          return next;
        });
        setManageTarget(null);
      },
    });
  };

  // 재생성 — 단건/일괄 모두 같은 확인 모달을 거친다. 신청한 상품만 선택에서 뺀다.
  const handleRetry = () => {
    if (!retryTargets) {
      return;
    }
    const retriedKeys = retryTargets.map(({ product }) =>
      toSelectionKey(product)
    );
    retryRequests(retryTargets, {
      onSuccess: () => {
        setSelectedProducts((prev) => {
          const next = new Map(prev);
          retriedKeys.forEach((key) => next.delete(key));
          return next;
        });
        setRetryTargets(null);
      },
    });
  };

  const handleChangeFilter = (filter: RequestFilter) => {
    setRequestFilter(filter);
    setPageNumber(1);
  };

  const handleChangeStatusTab = (tab: PathPickRequestStatusTab) => {
    setStatusTab(tab);
    setPageNumber(1);
  };

  const handleSearch = () => {
    setQuery(search.trim());
    setPageNumber(1);
  };

  // 두 축 중 하나라도 전체가 아니면 초기화 버튼을 노출한다 (검색어는 별도 축이라 건드리지 않는다).
  const isFilterActive = requestFilter !== "all" || statusTab !== "ALL";

  // 모바일 필터 버튼의 배지 숫자 — 전체가 아닌 축의 수(0~2).
  const appliedFilterCount =
    (requestFilter !== "all" ? 1 : 0) + (statusTab !== "ALL" ? 1 : 0);

  const handleResetFilters = () => {
    setRequestFilter("all");
    setStatusTab("ALL");
    setPageNumber(1);
  };

  const noInfoLabel = t(
    "path_pick:sync_download_history.product_requests_modal.no_info"
  );

  const formatDateTime = (iso: string | null) =>
    iso ? formatRequestDateTime(iso) : noInfoLabel;

  // 관리 열 버튼 — 연동 중인 API 연동 줄은 연동 해제, 재시도 가능한 실패 줄은 재생성.
  const renderManageButton = (
    result: PathPickProductRequestChannelResult,
    unsyncTarget: PathPickUnsyncTarget | null,
    product: PathPickMutationProduct | null
  ) => {
    if (result.channel === "smartStoreSync" && unsyncTarget) {
      return (
        <AppButton
          variant={"white"}
          onClick={() => setManageTarget(unsyncTarget)}
          {...lockedProps}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <CustomIcon
              src={"x-circle"}
              width={16}
              height={16}
              color={"#EE7D7A"}
            />
            <Text textStyle={"body-sm-bold"} color={"#EE7D7A"}>
              {t(
                "path_pick:sync_download_history.product_requests_modal.unsync"
              )}
            </Text>
          </Flex>
        </AppButton>
      );
    }

    if (
      IS_REQUEST_RETRY_ENABLED &&
      product &&
      isChannelResultRetryable(result)
    ) {
      return (
        <AppButton
          variant={"primary"}
          onClick={() => setRetryTargets([{ product, kinds: [result.kind] }])}
          {...lockedProps}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <CustomIcon
              src={"arrow-rotate"}
              width={16}
              height={16}
              color={"#FFFFFF"}
            />
            <Text textStyle={"body-sm-bold"}>
              {t(
                "path_pick:sync_download_history.product_requests_modal.retry"
              )}
            </Text>
          </Flex>
        </AppButton>
      );
    }

    // 걸 수 있는 액션이 없는 줄(연동 중이 아니고 재생성도 불가) — 표는 "-"로, 카드는 비워 둔다.
    return null;
  };

  // 빈 목록 안내 — 검색어가 가장 구체적인 원인이라 검색 → 필터 → 기본 순으로 고른다.
  const emptyState = query
    ? {
        titleKey: "path_pick:sync_download_history.no_search_results_title",
        descKey: "path_pick:sync_download_history.no_search_results_desc",
      }
    : isFilterActive
    ? {
        titleKey: "path_pick:sync_download_history.no_filter_results_title",
        descKey: "path_pick:sync_download_history.no_filter_results_desc",
      }
    : {
        titleKey: "path_pick:sync_download_history.no_products_title",
        descKey: null,
      };

  return (
    <>
      <CustomApprovalModal
        isOpen={isOpen}
        onClose={onClose}
        maxWidth={"90dvw"}
        // 화면이 좁으면 모달이 뷰포트를 넘지 않게 같이 내려온다 —
        // min-width가 max-width를 이기므로 여기서 함께 막아야 한다.
        minWidth={"min(1180px, 90dvw)"}
        // 상품 수·채널 줄 수·필터에 따라 높이가 들쭉날쭉하지 않게 항상 80dvh로 고정한다.
        // maxHeight는 scrollBehavior="inside"가 넣는 상한(100vh - 120px)을 덮기 위한 것 — 없으면
        // 세로 600px 미만 화면에서 80dvh가 지켜지지 않는다.
        height={"85dvh"}
        maxHeight={"85dvh"}
        scrollBehavior={"inside"}
        isCentered={true}
      >
        <CustomApprovalModal.Header
          onClose={onClose}
          title={
            <Flex alignItems={"center"} gap={"12px"} flexWrap={"wrap"}>
              <Text textStyle={"body-md-bold"} color={"#444444"}>
                {t(
                  "path_pick:sync_download_history.product_requests_modal.title"
                )}
              </Text>
              {/* TODO(BE): 완료 건수 집계 필드가 응답에 추가되면 "완료 N건" 칩을 함께 노출한다. */}
              <PathPickRequestStatusChip
                label={t(
                  "path_pick:sync_download_history.product_requests_modal.chip_products",
                  { count: totalCount }
                )}
              />
            </Flex>
          }
        />
        {/* 고정 높이 안에서 상품 목록만 늘고 줄게 — 스크롤 주인은 목록 하나뿐이다. */}
        <CustomApprovalModal.Body
          display={"flex"}
          flexDirection={"column"}
          overflow={"hidden"}
        >
          <Flex flexDirection={"column"} gap={"16px"} flex={1} minHeight={0}>
            {/* 검색·구분·상태를 한 줄에 둔다 — 남는 높이는 전부 목록에 넘긴다. */}
            <Flex
              gap={"12px"}
              alignItems={"center"}
              flexWrap={"wrap"}
              padding={"16px"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              flexShrink={0}
              justifyContent={"space-between"}
            >
              <Flex
                gap={"12px"}
                alignItems={"center"}
                flexWrap={"wrap"}
                /* 축소를 막으면 그룹이 max-content 폭을 유지해 안쪽 자식이 접힐 기회를
                   잃는다. minWidth 0까지 풀어야 검색창이 컨테이너를 따라 내려온다. */
                minWidth={0}
                flex={{ base: 1, md: "none" }}
              >
                {/* 모바일은 검색창과 필터 버튼만 한 줄에 둔다 — 검색은 키보드 검색(Enter)으로 제출하고,
                    건수는 헤더 칩과 아래 페이지네이션이 보여 준다. stretch로 필터 버튼이 검색창 실제 높이를 따라간다. */}
                <Flex
                  gap={"8px"}
                  alignItems={"stretch"}
                  width={{ base: "100%", md: "auto" }}
                >
                  {/* 검색창의 width는 문자열만 받아 반응형 폭은 바깥 Box가 맡는다.
                      데스크톱은 한 줄에 두 필터 축까지 들어가야 해 320px보다 좁게 잡는다. */}
                  <Box
                    flex={{ base: 1, md: "none" }}
                    minWidth={0}
                    width={{ base: "auto", md: "260px" }}
                  >
                    <SearchInput
                      value={search}
                      onChange={setSearch}
                      onKeyDown={handleSearch}
                      placeholder={t(
                        "path_pick:sync_download_history.product_requests_modal.search_placeholder"
                      )}
                      // 모바일 키보드의 엔터 키를 `검색`으로 보여준다 — 모바일엔 검색 버튼이 없다.
                      inputProps={{ enterKeyHint: "search" }}
                    />
                  </Box>
                  <PathPickRequestFilterModal.TriggerButton
                    appliedCount={appliedFilterCount}
                    onClick={() => setIsFilterModalOpen(true)}
                    height={"auto"}
                  />
                </Flex>
                {/* AppButton은 Tooltip으로 감싸여 있어 display 분기는 바깥 Flex에 건다. */}
                <Flex display={{ base: "none", md: "flex" }}>
                  <AppButton variant={"primary"} onClick={handleSearch}>
                    <Flex gap={"4px"} alignItems={"center"}>
                      <CustomIcon
                        src={"search"}
                        width={16}
                        height={16}
                        color={"#FFFFFF"}
                      />
                      <Text textStyle={"body-sm-bold"}>
                        {t(
                          "path_pick:sync_download_history.product_requests_modal.search"
                        )}
                      </Text>
                    </Flex>
                  </AppButton>
                </Flex>
                <Text
                  display={{ base: "none", md: "block" }}
                  textStyle={"body-sm-medium"}
                  color={"#444440"}
                >
                  {t(
                    "path_pick:sync_download_history.product_requests_modal.search_result",
                    { count: totalCount }
                  )}
                </Text>
                {/* 필터가 걸린 상태에서만 나타나는 텍스트 액션.
                    아래 인라인 필터 줄과 짝이라 데스크탑에만 남기고, 모바일에서는
                    필터 모달 안의 초기화가 대신한다. */}
                {isFilterActive && (
                  <Text
                    as={"button"}
                    type={"button"}
                    display={{ base: "none", md: "inline-block" }}
                    textStyle={"body-sm-medium"}
                    color={"#444440"}
                    _hover={{ color: "#1272EF" }}
                    onClick={handleResetFilters}
                  >
                    {t(
                      "path_pick:sync_download_history.product_requests_modal.reset_filter"
                    )}
                  </Text>
                )}
              </Flex>
              {/* 라벨과 탭 그룹은 쌍으로 묶는다 — 줄이 접혀도 라벨이 자기 탭에서 떨어지지 않는다.
                  좁은 폭에서는 탭이 5~6줄로 접혀 목록을 밀어내므로, 모바일에서는 이 줄을
                  숨기고 위의 필터 버튼 + 필터 모달로 같은 필터를 고른다. */}
              <Flex
                display={{ base: "none", md: "flex" }}
                gap={"12px"}
                alignItems={"center"}
                flexWrap={"wrap"}
              >
                <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
                  <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                    {t(
                      "path_pick:sync_download_history.product_requests_modal.filter_label_kind"
                    )}
                  </Text>
                  <PathPickRequestFilterTabs
                    tabs={REQUEST_FILTERS}
                    value={requestFilter}
                    labelKey={FILTER_LABEL_KEY}
                    onChange={handleChangeFilter}
                  />
                </Flex>
                <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
                  <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                    {t(
                      "path_pick:sync_download_history.product_requests_modal.filter_label_status"
                    )}
                  </Text>
                  <PathPickRequestFilterTabs
                    tabs={REQUEST_STATUS_TABS}
                    value={statusTab}
                    labelKey={REQUEST_STATUS_TAB_LABEL_KEY}
                    onChange={handleChangeStatusTab}
                  />
                </Flex>
              </Flex>
            </Flex>

            {/* 상단 액션 줄 — 재생성을 닫아 둔 동안 남는 일괄 동작은 연동 해제뿐이라,
                해제가 가능한 회원에게만 둔다(그 외에는 고를 수 있는 행이 아예 없다). */}
            {canUnsync && (
              <Flex
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={"16px"}
                flexWrap={"wrap"}
                // 목록에 높이를 돌려주려고 테두리·배경과 상하 패딩은 두지 않는다 —
                // 선택 여부는 칩 색(blue/gray)이 그대로 보여준다.
                padding={"0 10px"}
                flexShrink={0}
              >
                <PathPickRequestStatusChip
                  colorVariant={hasSelection ? "blue" : "gray"}
                  label={t(
                    "path_pick:sync_download_history.product_requests_modal.selected_chip",
                    { count: selectedProducts.size }
                  )}
                />
                <Flex gap={"12px"} alignItems={"center"} flexWrap={"wrap"}>
                  {showRetry && (
                    <AppButton
                      variant={"primary"}
                      isDisabled={bulkRetryTargets.length === 0}
                      onClick={() => setRetryTargets(bulkRetryTargets)}
                      {...lockedProps}
                    >
                      <Flex gap={"4px"} alignItems={"center"}>
                        <CustomIcon
                          src={"arrow-rotate"}
                          width={16}
                          height={16}
                          color={
                            bulkRetryTargets.length === 0
                              ? "#D4D4D1"
                              : "#FFFFFF"
                          }
                        />
                        {/* 대상이 없을 때 "선택 0개 재생성"이 되지 않게 카운트 없는 라벨을 쓴다. */}
                        <Text textStyle={"body-sm-bold"}>
                          {bulkRetryTargets.length === 0
                            ? t(
                                "path_pick:sync_download_history.product_requests_modal.retry"
                              )
                            : t(
                                "path_pick:sync_download_history.product_requests_modal.bulk_retry",
                                { count: bulkRetryTargets.length }
                              )}
                        </Text>
                      </Flex>
                    </AppButton>
                  )}
                  {showUnsync && (
                    <AppButton
                      variant={"white"}
                      isDisabled={bulkUnsyncProducts.length === 0}
                      onClick={() => setIsBulkConfirmOpen(true)}
                      {...lockedProps}
                    >
                      <Flex gap={"4px"} alignItems={"center"}>
                        <CustomIcon
                          src={"x-circle"}
                          width={16}
                          height={16}
                          color={
                            bulkUnsyncProducts.length === 0
                              ? "#8F8F8C"
                              : "#EE7D7A"
                          }
                        />
                        <Text
                          textStyle={"body-sm-bold"}
                          color={
                            bulkUnsyncProducts.length === 0
                              ? "#8F8F8C"
                              : "#EE7D7A"
                          }
                        >
                          {bulkUnsyncProducts.length === 0
                            ? t(
                                "path_pick:sync_download_history.product_requests_modal.unsync"
                              )
                            : t(
                                "path_pick:sync_download_history.product_requests_modal.bulk_unsync",
                                { count: bulkUnsyncProducts.length }
                              )}
                        </Text>
                      </Flex>
                    </AppButton>
                  )}
                  {/* Text 버튼에는 isDisabled가 없어 색·커서·핸들러로 비활성을 표현한다. */}
                  <Text
                    as={"button"}
                    type={"button"}
                    textStyle={"body-sm-medium"}
                    color={hasSelection ? "#444440" : "#D4D4D1"}
                    cursor={hasSelection ? "pointer" : "not-allowed"}
                    _hover={hasSelection ? { color: "#1272EF" } : undefined}
                    onClick={
                      hasSelection
                        ? () => setSelectedProducts(new Map())
                        : undefined
                    }
                  >
                    {t(
                      "path_pick:sync_download_history.product_requests_modal.clear_selection"
                    )}
                  </Text>
                </Flex>
              </Flex>
            )}

            {/* 모달의 남는 높이를 전부 흡수하는 유일한 가변 영역 */}
            <Flex
              flexDirection={"column"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              /* 표가 모달보다 넓으면 가로로 스크롤한다(카드는 폭에 맞춰 접혀 필요 없다).
                 세로 스크롤 주인은 아래 목록이라 여기서는 세로를 잠가 스크롤 축이 겹치지 않게 한다. */
              overflowX={isNarrow ? "hidden" : "auto"}
              overflowY={"hidden"}
              flex={1}
              minHeight={0}
            >
              {/* 헤더와 행이 같은 폭을 공유해야 가로 스크롤 중에도 열이 어긋나지 않는다. */}
              <Flex
                flexDirection={"column"}
                flex={1}
                minHeight={0}
                minWidth={isNarrow ? 0 : "fit-content"}
              >
                {/* 카드는 칸마다 라벨을 붙여 그리므로 칼럼 헤더 대신 전체 선택 줄만 둔다. */}
                {isNarrow ? (
                  canUnsync && (
                    <Flex
                      padding={"8px 12px"}
                      background={"#F7F9FA"}
                      borderBottom={"0.5px solid #D4D4D1"}
                      flexShrink={0}
                    >
                      <AppCheckBox
                        size={"small"}
                        name={t("common:select_all")}
                        isChecked={isAllSelected}
                        isIndeterminate={isIndeterminate}
                        isDisabled={selectableProducts.length === 0}
                        onChange={handleToggleAll}
                      />
                    </Flex>
                  )
                ) : (
                  <Grid
                    templateColumns={templateColumns}
                    padding={"12px 16px"}
                    background={"#F7F9FA"}
                    borderBottom={"0.5px solid #D4D4D1"}
                    alignItems={"center"}
                    flexShrink={0}
                  >
                    {COLUMNS.map((column) => {
                      const labelKey = COLUMN_LABEL_KEY[column];
                      if (column === "select") {
                        // 셀 요소를 만들지 않으면 접힌 트랙(0px)에 패딩도 붙지 않는다.
                        if (!canUnsync) {
                          return null;
                        }
                        return (
                          <Flex key={column} {...cellProps(column)}>
                            <AppCheckBox
                              size={"small"}
                              isChecked={isAllSelected}
                              isIndeterminate={isIndeterminate}
                              isDisabled={selectableProducts.length === 0}
                              onChange={handleToggleAll}
                            />
                          </Flex>
                        );
                      }
                      return (
                        <Text
                          key={column}
                          {...cellProps(column)}
                          textStyle={"body-sm-medium"}
                          color={"#8F8F8C"}
                        >
                          {labelKey
                            ? t(
                                `path_pick:sync_download_history.product_requests_modal.${labelKey}`
                              )
                            : ""}
                        </Text>
                      );
                    })}
                  </Grid>
                )}
                {isPending ? (
                  <CenteredSpinner flex={1} minHeight={0} />
                ) : (
                  <Box position={"relative"} flex={1} minHeight={0}>
                    <Flex
                      flexDirection={"column"}
                      height={"100%"}
                      overflowY={"auto"}
                    >
                      {/* 고정 높이 표가 설명 없이 비지 않게 검색/필터 상황별 안내를 둔다. */}
                      {items.length === 0 && (
                        <Flex
                          flex={1}
                          flexDirection={"column"}
                          justifyContent={"center"}
                          padding={"16px"}
                        >
                          <CustomNoResults
                            title={t(emptyState.titleKey)}
                            description={
                              emptyState.descKey
                                ? t(emptyState.descKey)
                                : undefined
                            }
                          />
                        </Flex>
                      )}
                      {items.map((item, index) => {
                        // 채널 결과가 없는 상품은 "확인 필요" 한 줄로 대체한다.
                        const rowCount = Math.max(
                          1,
                          item.visibleResults.length
                        );
                        // 요청 body에 담을 상품 참조 — 체크박스/관리 버튼/행 key가 함께 쓴다.
                        const product: PathPickMutationProduct | null =
                          item.productId !== null && item.productType !== null
                            ? { id: item.productId, type: item.productType }
                            : null;
                        const productKey = product
                          ? toSelectionKey(product)
                          : null;
                        const isSelected =
                          productKey !== null &&
                          selectedProducts.has(productKey);
                        const unsyncTarget: PathPickUnsyncTarget | null =
                          item.canUnsyncItem && product
                            ? {
                                ...product,
                                brandName: item.brandName,
                                name: item.name ?? item.modelNumber,
                                sku: item.sku,
                              }
                            : null;
                        // 옵션이 없으면 재고를 알 수 없는 상태라 합계 대신 null로 둔다.
                        const totalQuantity = item.options?.length
                          ? getTotalOptionQuantity(item.options)
                          : null;
                        const isSoldOut = totalQuantity === 0;

                        const stockLabel =
                          totalQuantity === null
                            ? noInfoLabel
                            : isSoldOut
                            ? t(
                                "path_pick:sync_download_history.product_requests_modal.stock_sold_out"
                              )
                            : totalQuantity.toLocaleString();
                        const stockColor = isSoldOut ? "#EE7D7A" : "#1D1D1B";

                        const checkbox = canUnsync && (
                          <AppCheckBox
                            size={"small"}
                            isChecked={isSelected}
                            isDisabled={!item.isSelectable}
                            onChange={() =>
                              product &&
                              handleToggleProduct({
                                ...product,
                                canUnsync: item.canUnsyncItem,
                                retryKinds: item.retryKinds,
                              })
                            }
                          />
                        );

                        const productInfo = (
                          <Flex gap={"12px"} alignItems={"center"} minWidth={0}>
                            <Image
                              src={item.imageUrl ?? ""}
                              fallbackSrc={"/icons-v1_1/null.png"}
                              boxSize={"48px"}
                              objectFit={"contain"}
                              borderRadius={"8px"}
                              border={"0.5px solid #EFEFED"}
                              background={"#FFFFFF"}
                              alt={item.name ?? item.modelNumber ?? ""}
                            />
                            <Flex
                              flexDirection={"column"}
                              gap={"2px"}
                              minWidth={0}
                            >
                              <Text
                                textStyle={"caption-medium"}
                                color={"#8F8F8C"}
                                noOfLines={1}
                                wordBreak={"break-all"}
                              >
                                {item.brandName ?? noInfoLabel}
                              </Text>
                              <PathPickCopyableSkuText
                                sku={item.sku}
                                emptyText={noInfoLabel}
                              />
                              {/* 상품명은 미기입인 경우가 많아 모델번호로 폴백한다. */}
                              <Text
                                textStyle={"body-md-bold"}
                                color={"#1D1D1B"}
                                noOfLines={1}
                                wordBreak={"break-all"}
                              >
                                {item.name ?? item.modelNumber ?? noInfoLabel}
                              </Text>
                            </Flex>
                          </Flex>
                        );

                        // 채널별 줄 — 구분 / 상태 / 비고 / 신청일 / 최근 업데이트 / 관리.
                        // 표와 카드가 같은 값을 받도록 줄 props는 여기서 한 번만 만든다.
                        const channelRows: ChannelProps[] =
                          item.visibleResults.length === 0
                            ? [
                                {
                                  id: "needs-check",
                                  kindLabel: noInfoLabel,
                                  // 어떤 신청인지 모르는 줄이라 아이콘을 붙이지 않는다.
                                  kindIcon: null,
                                  badge: (
                                    <PathPickBadge
                                      label={t(
                                        "path_pick:sync_download_history.product_requests_modal.status_needs_check"
                                      )}
                                      colorVariant={"yellow"}
                                    />
                                  ),
                                  isFailed: false,
                                  requestedAtLabel: formatDateTime(
                                    item.createdAt
                                  ),
                                  updatedAtLabel: noInfoLabel,
                                  manageButton: null,
                                },
                              ]
                            : item.visibleResults.map((result) => ({
                                id: result.channel,
                                kindIcon: CHANNEL_ICON[result.channel],
                                kindLabel: t(
                                  `path_pick:sync_download_history.product_requests_modal.${
                                    CHANNEL_LABEL_KEY[result.channel]
                                  }`
                                ),
                                badge: <ChannelBadge result={result} />,
                                isFailed: isChannelResultFailed(result),
                                failureNoteKey: getFailureNoteKey(
                                  result.errorType
                                ),
                                requestedAtLabel: formatDateTime(
                                  result.requestedAt ?? item.createdAt
                                ),
                                updatedAtLabel: formatDateTime(
                                  result.updatedAt ?? item.updatedAt
                                ),
                                manageButton: renderManageButton(
                                  result,
                                  unsyncTarget,
                                  product
                                ),
                              }));

                        if (isNarrow) {
                          return (
                            <Flex
                              // 상품 참조가 nullable이라 없으면 인덱스로 대체한다.
                              key={productKey ?? `row-${index}`}
                              flexDirection={"column"}
                              gap={"8px"}
                              padding={"12px"}
                              // 상품 경계는 채널 경계(0.5px #EFEFED)보다 진하게 — 어디서 상품이 바뀌는지 보이게 한다.
                              borderTop={
                                index > 0 ? "1px solid #D4D4D1" : undefined
                              }
                            >
                              <Flex
                                gap={"8px"}
                                alignItems={"center"}
                                minWidth={0}
                              >
                                {checkbox}
                                {productInfo}
                              </Flex>
                              <Flex gap={"6px"} alignItems={"baseline"}>
                                <Text
                                  textStyle={"caption-medium"}
                                  color={"#8F8F8C"}
                                >
                                  {t(
                                    "path_pick:sync_download_history.product_requests_modal.column_stock"
                                  )}
                                </Text>
                                <Text
                                  textStyle={"body-sm-bold"}
                                  color={stockColor}
                                >
                                  {stockLabel}
                                </Text>
                              </Flex>
                              <Flex flexDirection={"column"}>
                                {channelRows.map(
                                  ({ id, ...channel }, rowIndex) => (
                                    <ChannelCard
                                      key={id}
                                      {...channel}
                                      hasBorderTop={rowIndex > 0}
                                    />
                                  )
                                )}
                              </Flex>
                            </Flex>
                          );
                        }

                        return (
                          <Grid
                            // 상품 참조가 nullable이라 없으면 인덱스로 대체한다.
                            key={productKey ?? `row-${index}`}
                            templateColumns={templateColumns}
                            // 줄 높이를 트랙으로 고정해 버튼 유무와 무관하게 모든 채널 줄이 같은 높이가 된다.
                            templateRows={`repeat(${rowCount}, minmax(${CHANNEL_ROW_HEIGHT}, auto))`}
                            padding={"8px 16px"}
                            // 상품 경계는 채널 경계(0.5px #EFEFED)보다 진하게 — 어디서 상품이 바뀌는지 보이게 한다.
                            borderTop={
                              index > 0 ? "1px solid #D4D4D1" : undefined
                            }
                          >
                            {checkbox && (
                              <Flex
                                {...cellProps("select")}
                                gridRow={`1 / span ${rowCount}`}
                                alignItems={"center"}
                              >
                                {checkbox}
                              </Flex>
                            )}

                            <Flex
                              {...cellProps("product")}
                              gridRow={`1 / span ${rowCount}`}
                              alignItems={"center"}
                              minWidth={0}
                            >
                              {productInfo}
                            </Flex>

                            {/* 현재 재고 — 상품 단위 값이라 채널 줄 전체를 세로로 합친다.
                              옵션이 없으면 재고를 알 수 없는 상태라 "-"로 둔다.
                              합계가 0이면 숫자 대신 품절 문구로 상태를 바로 읽히게 한다. */}
                            <Text
                              {...cellProps("stock")}
                              gridRow={`1 / span ${rowCount}`}
                              alignSelf={"center"}
                              textStyle={"body-md-medium"}
                              color={stockColor}
                              noOfLines={1}
                            >
                              {stockLabel}
                            </Text>

                            {channelRows.map(({ id, ...channel }, rowIndex) => (
                              <ChannelCells
                                key={id}
                                {...channel}
                                row={rowIndex + 1}
                              />
                            ))}
                          </Grid>
                        );
                      })}
                    </Flex>
                    {/* 페이지/필터 전환 중에는 이전 목록 위에 오버레이 스피너만 덮는다 */}
                    {isPlaceholderData && (
                      <Box
                        position={"absolute"}
                        inset={0}
                        background={"rgba(255, 255, 255, 0.6)"}
                      >
                        <CenteredSpinner />
                      </Box>
                    )}
                  </Box>
                )}
              </Flex>
            </Flex>

            {/* 0건이면 "총 0개 중 0-0개"만 남으므로 빈 상태에서는 감춘다. */}
            {totalCount > 0 && (
              <Box flexShrink={0}>
                <PathPickPagination
                  total={totalCount}
                  pageNumber={pageNumber}
                  pageSize={pageSize}
                  onChangePage={setPageNumber}
                  onChangePageSize={(size) => {
                    setPageSize(size);
                    setPageNumber(1);
                  }}
                />
              </Box>
            )}
          </Flex>
        </CustomApprovalModal.Body>
      </CustomApprovalModal>

      {isFilterModalOpen && (
        <PathPickRequestFilterModal
          isOpen
          onClose={() => setIsFilterModalOpen(false)}
          isFilterActive={isFilterActive}
          onReset={handleResetFilters}
          resetLabel={t(
            "path_pick:sync_download_history.product_requests_modal.reset_filter"
          )}
        >
          {/* 인라인 줄과 완전히 같은 인자를 넘긴다 — 상태와 핸들러를 공유하므로
              어느 쪽에서 고르든 결과가 같다. */}
          <PathPickRequestFilterModal.Group
            label={t(
              "path_pick:sync_download_history.product_requests_modal.filter_label_kind"
            )}
          >
            <PathPickRequestFilterTabs
              tabs={REQUEST_FILTERS}
              value={requestFilter}
              labelKey={FILTER_LABEL_KEY}
              onChange={handleChangeFilter}
            />
          </PathPickRequestFilterModal.Group>
          <PathPickRequestFilterModal.Group
            label={t(
              "path_pick:sync_download_history.product_requests_modal.filter_label_status"
            )}
          >
            <PathPickRequestFilterTabs
              tabs={REQUEST_STATUS_TABS}
              value={statusTab}
              labelKey={REQUEST_STATUS_TAB_LABEL_KEY}
              onChange={handleChangeStatusTab}
            />
          </PathPickRequestFilterModal.Group>
        </PathPickRequestFilterModal>
      )}

      {isBulkConfirmOpen && (
        <PathPickUnsyncConfirmModal
          isOpen
          onClose={() => setIsBulkConfirmOpen(false)}
          count={bulkUnsyncProducts.length}
          isLoading={isUnsyncing}
          onUnsyncOnly={() => handleBulkUnsync(false)}
          onDelete={() => handleBulkUnsync(true)}
        />
      )}

      {manageTarget && (
        <PathPickManageProductModal
          isOpen
          onClose={() => setManageTarget(null)}
          target={manageTarget}
          isLoading={isUnsyncing}
          onUnsyncOnly={() => handleManageUnsync(false)}
          onDelete={() => handleManageUnsync(true)}
        />
      )}

      {retryTargets && (
        <PathPickRetryConfirmModal
          isOpen
          onClose={() => setRetryTargets(null)}
          count={retryTargets.length}
          isLoading={isRetrying}
          onConfirm={handleRetry}
        />
      )}
    </>
  );
}

// 관리 칼럼에 걸 동작이 없을 때의 표기 — 비고 칼럼의 "-"와 같은 글자·색을 쓴다.
const ManagePlaceholder = () => (
  <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
    {"-"}
  </Text>
);

interface ChannelBadgeProps {
  result: PathPickProductRequestChannelResult;
}

// 채널 결과 배지 — 문구/색은 요청 단위 표기(getProductRequestChannelStatusMeta)를 따른다.
// 연동 해제/상품 삭제로 끝난 상품은 처리 상태 대신 종료 상태(연동 해제/상품 삭제)를 보여준다.
const ChannelBadge = ({ result }: ChannelBadgeProps) => {
  const { t } = useI18nHelper();
  const meta =
    getPathPickSyncStatusMeta(result.syncStatus, result.syncDeletedBy) ??
    getProductRequestChannelStatusMeta(result.channel, result.status);

  return (
    <PathPickBadge
      label={t(`path_pick:sync_download_history.${meta.labelKey}`)}
      colorVariant={meta.colorVariant}
      leadingIcon={meta.leadingIcon}
    />
  );
};

// 채널 한 줄의 값 — 표(ChannelCells)와 모바일 카드(ChannelCard)가 같은 값을 받는다.
interface ChannelProps {
  // 줄 식별자 — 채널 이름, 결과가 없는 "확인 필요" 줄은 고정값
  id: string;
  kindLabel: string;
  // 구분 문구 앞 아이콘 — 채널 결과가 없는 줄은 null
  kindIcon: AssetTypes | null;
  badge: React.ReactNode;
  // 배지가 실패인 줄인지 — 비고 칸의 안내 문구 노출을 가른다.
  isFailed: boolean;
  // 실패 줄의 비고 문구(사진 없음·마켓에 없는 브랜드·문의 안내) — 실패가 아니면 쓰지 않는다.
  failureNoteKey?: PathPickFailureNoteKey;
  requestedAtLabel: string;
  updatedAtLabel: string;
  // 이 줄에서 걸 수 있는 동작 — 연동 해제 또는 재생성, 없으면 null
  manageButton: React.ReactNode;
}

interface ChannelCellsProps extends Omit<ChannelProps, "id"> {
  // 상품 그리드 안에서 이 채널이 차지할 행 번호 (1-base)
  row: number;
}

interface ChannelCardProps extends Omit<ChannelProps, "id"> {
  // 카드 안 두 번째 채널부터 위쪽 구분선을 그린다.
  hasBorderTop: boolean;
}

interface ChannelKindLabelProps {
  label: string;
  icon: AssetTypes | null;
}

// 구분 문구 — 표와 카드가 같은 모양(아이콘 + 굵은 문구)으로 쓴다.
const ChannelKindLabel = ({ label, icon }: ChannelKindLabelProps) => (
  <Flex gap={"4px"} alignItems={"center"} minWidth={0}>
    {icon && (
      <Flex flexShrink={0}>
        <CustomIcon src={icon} width={16} height={16} color={"#8F8F8C"} />
      </Flex>
    )}
    <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
      {label}
    </Text>
  </Flex>
);

// 채널 한 줄이 채우는 셀들 — 상품 단위 병합 셀과 어긋나지 않도록 행/열을 모두 명시한다.
const ChannelCells = ({
  row,
  kindLabel,
  kindIcon,
  badge,
  isFailed,
  failureNoteKey = "note_contact_support",
  requestedAtLabel,
  updatedAtLabel,
  manageButton,
}: ChannelCellsProps) => {
  const { t } = useI18nHelper();

  // 두 번째 줄부터는 채널 셀에만 구분선을 그린다 (선택/상품은 세로로 병합된 셀이다).
  // 세로 패딩을 두면 버튼 줄만 행 트랙(CHANNEL_ROW_HEIGHT)을 넘겨 다시 높이가 어긋나므로 넣지 않는다.
  const rowStyle = {
    gridRow: row,
    alignItems: "center",
    borderTop: row > 1 ? "0.5px solid #EFEFED" : undefined,
  } as const;

  return (
    <>
      <Flex {...rowStyle} {...cellProps("kind")}>
        <ChannelKindLabel label={kindLabel} icon={kindIcon} />
      </Flex>
      <Flex {...rowStyle} {...cellProps("status")}>
        {badge}
      </Flex>
      {/* 서버 오류 코드는 사용자가 스스로 조치할 수 없어, 실패 줄에는 문의 안내만 둔다.
          좁은 칸에서 잘리지 않게 줄바꿈으로 흘린다. */}
      <Flex {...rowStyle} {...cellProps("note")}>
        <Text
          textStyle={"body-sm-medium"}
          color={isFailed ? "#EE7D7A" : "#8F8F8C"}
          wordBreak={"break-word"}
        >
          {isFailed
            ? t(
                `path_pick:sync_download_history.product_requests_modal.${failureNoteKey}`
              )
            : "-"}
        </Text>
      </Flex>
      <Flex {...rowStyle} {...cellProps("requestedAt")}>
        <Text textStyle={"body-sm-medium"} color={"#444440"}>
          {requestedAtLabel}
        </Text>
      </Flex>
      <Flex {...rowStyle} {...cellProps("updatedAt")}>
        <Text textStyle={"body-sm-medium"} color={"#444440"}>
          {updatedAtLabel}
        </Text>
      </Flex>
      <Flex {...rowStyle} {...cellProps("manage")}>
        {manageButton ?? <ManagePlaceholder />}
      </Flex>
    </>
  );
};

// 모바일 카드 안 채널 한 블록 — 구분·상태를 한 줄에, 날짜를 그 아래에, 실패 사유와 관리 버튼은 있을 때만 붙인다.
const ChannelCard = ({
  kindLabel,
  kindIcon,
  badge,
  isFailed,
  failureNoteKey = "note_contact_support",
  requestedAtLabel,
  updatedAtLabel,
  manageButton,
  hasBorderTop,
}: ChannelCardProps) => {
  const { t } = useI18nHelper();

  const dateLines = [
    { labelKey: "column_requested_at", value: requestedAtLabel },
    { labelKey: "column_updated_at", value: updatedAtLabel },
  ];

  return (
    <Flex
      flexDirection={"column"}
      gap={"6px"}
      padding={"10px 0"}
      borderTop={hasBorderTop ? "0.5px solid #EFEFED" : undefined}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <ChannelKindLabel label={kindLabel} icon={kindIcon} />
        {badge}
      </Flex>
      <Flex columnGap={"12px"} rowGap={"2px"} flexWrap={"wrap"}>
        {dateLines.map(({ labelKey, value }) => (
          <Flex key={labelKey} gap={"6px"} alignItems={"baseline"}>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t(
                `path_pick:sync_download_history.product_requests_modal.${labelKey}`
              )}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#444440"}>
              {value}
            </Text>
          </Flex>
        ))}
      </Flex>
      {/* 서버 오류 코드는 사용자가 스스로 조치할 수 없어, 실패 줄에는 문의 안내만 둔다. */}
      {isFailed && (
        <Text
          textStyle={"body-sm-medium"}
          color={"#EE7D7A"}
          wordBreak={"break-word"}
        >
          {t(
            `path_pick:sync_download_history.product_requests_modal.${failureNoteKey}`
          )}
        </Text>
      )}
      {/* 관리 동작이 없는 줄은 버튼 자리를 비운다 — 표처럼 칸을 채울 "-"가 필요 없다. */}
      {manageButton && <Flex justifyContent={"flex-end"}>{manageButton}</Flex>}
    </Flex>
  );
};

export default PathPickSyncDownloadProductRequestsModal;
